/** Field note pada baris list GET /WA/meeting-schedules (nama field bisa bervariasi). */
export function getScheduleRowNote(record) {
  if (!record) return "";
  return (
    record.note ??
    record.schedule_note ??
    record.description ??
    ""
  );
}

/** ID status pada baris list (nama field bisa bervariasi). */
export function getScheduleRowStatusId(record) {
  if (!record) return null;
  if (record.status_id != null) return Number(record.status_id);
  if (record.meeting_schedule_status_id != null) {
    return Number(record.meeting_schedule_status_id);
  }
  if (record.status?.id != null) return Number(record.status.id);
  return null;
}

export function getStatusMeta(statuses, statusId) {
  if (statusId == null || !statuses?.length) return null;
  return (
    statuses.find((s) => Number(s.id) === Number(statusId)) ?? null
  );
}

export function formatStatusLabel(meta, locale) {
  if (!meta) return "";
  const loc = locale === "en-US" ? "en-US" : "id-ID";
  return meta.names?.[loc] ?? meta.name ?? meta.code ?? "";
}

/** Warna UI dari GET statuses untuk `status_id` pada record jadwal. */
export function getBookedScheduleDisplayColor(
  record,
  scheduleStatuses,
  fallback = null
) {
  const statusId = getScheduleRowStatusId(record);
  if (statusId == null || !scheduleStatuses?.length) return fallback;
  const meta = getStatusMeta(scheduleStatuses, statusId);
  return meta?.color || fallback;
}

function statusMetaSearchBlob(meta) {
  if (!meta) return "";
  const code = String(meta.code ?? "").toLowerCase();
  const labelEn = String(formatStatusLabel(meta, "en-US")).toLowerCase();
  const labelId = String(formatStatusLabel(meta, "id-ID")).toLowerCase();
  return `${code} ${labelEn} ${labelId}`;
}

/**
 * Status "dibatalkan" dari meta GET statuses. Tanpa statuses terisi, return false.
 */
export function isCancelledScheduleRecord(record, scheduleStatuses) {
  if (!record || !scheduleStatuses?.length) return false;
  const statusId = getScheduleRowStatusId(record);
  if (statusId == null) return false;
  const meta = getStatusMeta(scheduleStatuses, statusId);
  if (!meta) return false;
  const code = String(meta.code ?? "").toLowerCase();
  if (
    code === "cancelled" ||
    code === "canceled" ||
    code === "cancel" ||
    code === "batal" ||
    code === "void"
  ) {
    return true;
  }
  const blob = statusMetaSearchBlob(meta);
  if (blob.includes("dibatalkan") || blob.includes("batal")) return true;
  if (/\bcancel(l(ed|ing|ation)?)?\b/.test(blob)) return true;
  return false;
}

export function filterMeetingSchedulesNotCancelled(
  meeting_schedules,
  scheduleStatuses
) {
  if (!Array.isArray(meeting_schedules) || meeting_schedules.length === 0) {
    return meeting_schedules || [];
  }
  if (!scheduleStatuses?.length) return meeting_schedules;
  return meeting_schedules.filter(
    (s) => !isCancelledScheduleRecord(s, scheduleStatuses)
  );
}

/** Salin data kalender bulan dengan `meeting_schedules` tanpa status batal. */
export function filterCalendarMonthWithoutCancelledMeetings(
  calendarData,
  scheduleStatuses
) {
  if (!calendarData || !scheduleStatuses?.length) return calendarData;
  const out = { ...calendarData };
  for (const key of Object.keys(out)) {
    const day = out[key];
    if (!day?.meeting_schedules?.length) continue;
    out[key] = {
      ...day,
      meeting_schedules: filterMeetingSchedulesNotCancelled(
        day.meeting_schedules,
        scheduleStatuses
      ),
    };
  }
  return out;
}

export function isOverlapActiveScheduleRecord(record, scheduleStatuses) {
  if (!record) return true;
  if (!scheduleStatuses?.length) return true;
  const statusId = getScheduleRowStatusId(record);
  if (statusId == null) return true;
  const meta = getStatusMeta(scheduleStatuses, statusId);
  if (!meta) return true;
  return meta.is_active_for_overlap !== false;
}

export function filterCalendarMonthOnlyOverlapActive(
  calendarData,
  scheduleStatuses
) {
  if (!calendarData || !scheduleStatuses?.length) return calendarData;
  const out = { ...calendarData };
  for (const key of Object.keys(out)) {
    const day = out[key];
    if (!day?.meeting_schedules?.length) continue;
    out[key] = {
      ...day,
      meeting_schedules: day.meeting_schedules.filter((s) =>
        isOverlapActiveScheduleRecord(s, scheduleStatuses)
      ),
    };
  }
  return out;
}

/**
 * Baris list GET meeting-schedules sering menyimpan kontak sebagai string (nama tampilan),
 * tanpa `status: "booked"` atau label meta yang mengandung substring "book".
 */
export function hasScheduleRowListContact(record) {
  if (!record) return false;
  const c = record.contact;
  if (typeof c === "string") {
    const s = c.trim();
    if (!s) return false;
    if (s === "—" || s === "-" || s === "–") return false;
    return true;
  }
  if (c && typeof c === "object") {
    if (c.id != null) return true;
    if (String(c.name ?? "").trim() !== "") return true;
  }
  return false;
}

/**
 * UI "booked" (kontak + keterangan + hapus/edit): prioritas contact dari detail API;
 * lalu cocokkan meta status dari GET statuses (code/nama); fallback legacy status === 2.
 */
export function showBookedScheduleUi({ data, scheduleDetail, scheduleStatuses }) {
  if (data?.status === "booked") return true;
  if (scheduleDetail?.contact) return true;
  if (hasScheduleRowListContact(data)) return true;
  const merged = { ...(data || {}), ...(scheduleDetail || {}) };
  const statusId = getScheduleRowStatusId(merged);
  if (scheduleStatuses?.length && statusId != null) {
    const meta = getStatusMeta(scheduleStatuses, statusId);
    const blob = statusMetaSearchBlob(meta);
    if (blob.includes("book")) return true;
  }
  return Number(data?.status) === 2;
}

/**
 * Slot tersedia (set unavailable / add schedule), fallback legacy status === 1.
 */
export function showAvailableSlotUi({ data, scheduleDetail, scheduleStatuses }) {
  if (showBookedScheduleUi({ data, scheduleDetail, scheduleStatuses })) {
    return false;
  }
  if (data?.status === "available") return true;
  const merged = { ...(data || {}), ...(scheduleDetail || {}) };
  const statusId = getScheduleRowStatusId(merged);
  if (scheduleStatuses?.length && statusId != null) {
    const meta = getStatusMeta(scheduleStatuses, statusId);
    const blob = statusMetaSearchBlob(meta);
    if (
      blob.includes("avail") ||
      blob.includes("open") ||
      blob.includes("free") ||
      blob.includes("tersedia")
    ) {
      return true;
    }
  }
  return Number(data?.status) === 1;
}
